import { Eye, EyeOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { device } from "../../utils/helperFunctions";

function Input({
  value: propValue = "",
  onChange,
  type: propType = "text",
  placeholder = "",
  maxLength,
  readOnly,
  autoComplete = "off",
  autoFocus,
  disabled,
  width = "100%",
  height = "100%",
  style = {},
  showEyeToggle = true,
  ...rest
}) {
  const [isVisible, setIsVisible] = useState(true);
  const [internalValue, setInternalValue] = useState(propValue);
  const [lastTypedIndex, setLastTypedIndex] = useState(null);
  const prevValueRef = useRef(propValue);

  const inputRef = useRef(null);

  useEffect(() => {
    setInternalValue(propValue);
    prevValueRef.current = propValue;
  }, [propValue]);

  const handleChange = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart;

    let newValue = e.target.value;
    const prevValue = prevValueRef.current;

    if (maxLength) newValue = newValue.slice(0, maxLength);

    let newIndex = null;
    for (let i = 0; i < newValue.length; i++) {
      if (prevValue[i] !== newValue[i]) {
        newIndex = i;
        break;
      }
    }
    if (newValue.length < prevValue.length) newIndex = null;

    setInternalValue(newValue);
    setLastTypedIndex(newIndex);
    prevValueRef.current = newValue;

    setTimeout(() => {
      input.setSelectionRange(cursorPos, cursorPos);
    }, 0);

    onChange?.({
      ...e,
      target: { ...e.target, value: newValue },
    });
  };

  const getDisplayValue = () => {
    if (!internalValue) return "";

    const value = maxLength ? internalValue.slice(0, maxLength) : internalValue;

    const lastIndex =
      lastTypedIndex != null && lastTypedIndex < value.length
        ? lastTypedIndex
        : null;

    if (isVisible) return value;

    return value
      .split("")
      .map((ch, i) => (i === lastIndex ? ch : "•"))
      .join("");
  };

  const displayValue = getDisplayValue();
  const actualType =
    propType === "password" ? (isVisible ? "text" : "password") : propType;

  return (
    <Wrapper style={{ width, height }}>
      <HiddenInput
        ref={inputRef}
        type={actualType}
        value={internalValue}
        onChange={handleChange}
        placeholder={placeholder}
        maxLength={maxLength}
        readOnly={readOnly}
        autoCorrect="off"
        autoComplete="new-password"
        autoCapitalize="off"
        spellCheck={false}
        autoFocus={autoFocus}
        disabled={disabled}
        name={`input-${Date.now()}`}
        style={style}
        {...rest}
      />
      {displayValue.length > 0 && (
        <Overlay style={style}>
          <DisplayText isPlaceholder={!displayValue}>
            {displayValue || placeholder}
          </DisplayText>
        </Overlay>
      )}
      {showEyeToggle && (
        <IconButton
          type="button"
          onClick={() => {
            if (inputRef.current) {
              const cursorPos = inputRef.current.selectionStart;
              setIsVisible(!isVisible);

              setTimeout(() => {
                inputRef.current.setSelectionRange(cursorPos, cursorPos);
                inputRef.current.focus();
              }, 0);
            } else {
              setIsVisible(!isVisible);
            }
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {isVisible ? <Eye size={32} /> : <EyeOff size={32} />}
        </IconButton>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const sharedTextStyles = css`
  font-size: 40px;
  font-weight: 500;
  font-family: var(--font-primary);
  line-height: 55px;
`;

const ICON_SPACE = "60px";

const HiddenInput = styled.input`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "100%"};
  border: ${(props) => props.border || "none"};
  ${sharedTextStyles};

  background: var(--input);

  caret-color: var(--text-primary);
  color: transparent;

  box-sizing: border-box;
  padding-right: ${ICON_SPACE};

  &:focus {
    outline: none;
  }

  ::placeholder {
    font-weight: 500;
    font-size: 40px;
    line-height: 55px;
    color: #222222;
    opacity: 0.3;

    @media ${device.tablet} {
      font-size: 20px;
      line-height: 20px;
    }
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${(props) => props.height || "100%"};
  border: none !important;

  pointer-events: none;
  display: flex;
  align-items: center;
  z-index: 0;
  box-sizing: border-box;

  padding-right: ${ICON_SPACE};

  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const DisplayText = styled.span`
  ${sharedTextStyles};
  color: ${(props) =>
    props.isPlaceholder ? "rgba(34, 34, 34, 0.3)" : "var(--text-primary)"};
  white-space: nowrap;
`;

// Eye icon toggle
const IconButton = styled.button`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #666;
  }
`;

export default Input;
